// Admin user-management DTOs (§4.4 Users (Admin)). The api wraps the
// Keycloak Admin REST so the shapes here mirror the KC user model trimmed
// to the fields the admin UI actually edits.
//
// `email` is AUTH-02 immutable post-creation — `AdminUserUpdate` therefore
// has NO `email` field on purpose.
// `phoneNumber` is AUTH-05 load-bearing for SMS notifications — required on
// create, kept E.164.
// `state` is AUTH-06: invited → active → deactivated (KC `enabled=false`
// soft-flag; preserves audit references per the admin-user-management card).
// `notificationsEnabled` is CHR-03 §1 (ADR-0045): a per-user SMS opt-out,
// stored as a KC user attribute mirroring `phoneNumber`. Default ON
// (absent ⇒ true server-side). A user with it OFF is dropped as the final
// step of notification recipient resolution regardless of role/assignment.

export type UserState = 'POZVANY' | 'AKTIVNI' | 'DEAKTIVOVANY';
// CHR-03 §2 / ADR-0046 — `viewer` (UI „viewer") is the fourth assignable
// realm role: app-wide read-only.
export type UserRole = 'admin' | 'backoffice' | 'dispatcher' | 'viewer';

export const USER_STATES: readonly UserState[] = [
  'POZVANY',
  'AKTIVNI',
  'DEAKTIVOVANY',
];

export const USER_ROLES: readonly UserRole[] = [
  'admin',
  'backoffice',
  'dispatcher',
  'viewer',
];

export interface AdminUserDto {
  sub: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  roles: UserRole[];
  state: UserState;
  notificationsEnabled: boolean;
}

export interface AdminUserCreate {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  roles: UserRole[];
}

export interface AdminUserUpdate {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  roles?: UserRole[];
  // ⚠ Full-replace PATCH: the api defaults an ABSENT `notificationsEnabled`
  // back to `true` server-side. The editor MUST therefore always include the
  // current value on every save — omitting it would silently re-enable a
  // disabled user when any other field is edited (CHR-03 §1 / ADR-0045).
  notificationsEnabled?: boolean;
}
